import type {
  ScheduleActivityActor,
  ScheduleActivityItem,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_activity.pb';
import { UsersQueries } from '@frontend/api-users';
import { i18next } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { UsersTypes } from '@frontend/user-helpers';
import { Text } from '@frontend/design-system';
import { StatusBadge } from '../status-badge';
import { activitySourceTypeLabels } from './constants';
import type { ActivitySourceType } from './types';

const getActorText = (
  activitySourceType: ActivitySourceType,
  actor: ScheduleActivityActor,
  locationUsers: UsersTypes.UserProfile[]
): string => {
  switch (actor.actorType) {
    case 'STAFF': {
      const userDetails = locationUsers.find((user) => user.UserID === actor.actorUserId);
      if (activitySourceType === 'STAFF_UI') {
        return actor.actorUserName
          ? i18next.t('By {{actorUserName}}', {
              ns: 'schedule-activity',
              actorUserName: actor.actorUserName || getFullName(userDetails ?? {}),
            })
          : '';
      }
      return i18next.t('Via Schedule Request', { ns: 'schedule-activity' });
    }
    case 'PATIENT': {
      if (activitySourceType === 'BOOKING_SITE') {
        return i18next.t('Via online scheduling', { ns: 'schedule-activity' });
      }
      if (activitySourceType === 'WAITLIST') {
        return i18next.t('Via Waitlist', { ns: 'schedule-activity' });
      }
      return actor.actorUserName || '';
    }
    default:
      return '';
  }
};

export const SourceCellRenderer = ({ row }: { row: ScheduleActivityItem }) => {
  const locationUsers = UsersQueries.useGetMultiLocationsUsers({
    locationIds: [row.locationId ?? ''],
    params: { showChildren: false },
  }).flatMap(({ data }) => data || []);

  if (!row?.activitySourceType || !row?.actor) {
    return <Text size='medium'>-</Text>;
  }

  if (row.activitySourceType === 'AI_VOICE' || row.activitySourceType === 'AI_TEXT') {
    return <StatusBadge variant='ai-added' />;
  }

  return (
    <div css={{ display: 'flex', flexDirection: 'column' }}>
      <Text>{activitySourceTypeLabels[row?.activitySourceType as ActivitySourceType] || row?.activitySourceType}</Text>
      <Text size='medium'>{getActorText(row?.activitySourceType as ActivitySourceType, row.actor, locationUsers)}</Text>
    </div>
  );
};
